import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Searchable BAS account picker.
 *
 * Replaces the 4-digit text input in TemplateForm for inputs of type
 * `account_picker`. A non-expert user can type either a number ("19")
 * or a name ("företagskonto") and pick from a filtered dropdown that
 * shows both — e.g. "1930 — Företagskonto/checkkonto/affärskonto".
 *
 * Props:
 *   id           HTML id attached to the underlying input.
 *   value        current account number (4-digit string) or ''.
 *   onChange     (newValue) => void
 *   accounts     full tenant chart of accounts. Shape:
 *                  [{ accountNumber, accountName, accountType, ... }]
 *                  (the wizard's fetchAccounts populates this).
 *   filter       optional filter expression from the template body's
 *                input declaration. Supported syntax:
 *                  "class:4"      first digit is 4
 *                  "class:4-6"    first digit between 4 and 6 inclusive
 *                  "class:1,2,3"  first digit is 1 OR 2 OR 3
 *                Unknown filter strings fall back to no filter (safe
 *                default; the user sees all accounts).
 *   required     bool
 *   disabled     bool
 *
 * Accessibility:
 *   - role="combobox" with aria-expanded / aria-controls / aria-activedescendant
 *   - listbox options have role="option" with aria-selected
 *   - keyboard: ↑/↓ navigate, Enter selects, Esc closes
 */
function BasAccountPicker({ id, value, onChange, accounts = [], filter, required, disabled }) {
    const filtered = useMemo(() => applyFilter(accounts, filter), [accounts, filter]);

    const selected = useMemo(
        () => filtered.find((a) => a.accountNumber === value)
            || accounts.find((a) => a.accountNumber === value)
            || null,
        [filtered, accounts, value],
    );

    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapperRef = useRef(null);

    // When `value` changes (e.g. cleared by parent), reset the visible
    // query string back to a presentational label so users see what
    // they picked.
    useEffect(() => {
        if (!open) setQuery(selected ? `${selected.accountNumber} — ${selected.accountName}` : '');
    }, [selected, open]);

    // Close on outside click.
    useEffect(() => {
        function onDocClick(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        // If the query is a presentational label for the current
        // selection, treat it as empty (no narrowing).
        if (selected && q === `${selected.accountNumber} — ${selected.accountName}`.toLowerCase()) {
            return filtered;
        }
        if (!q) return filtered;
        return filtered.filter((a) =>
            a.accountNumber.startsWith(q)
            || (a.accountName && a.accountName.toLowerCase().includes(q)),
        );
    }, [filtered, query, selected]);

    const handleKeyDown = (e) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
            setOpen(true);
            setActiveIndex(0);
            e.preventDefault();
            return;
        }
        if (!open) return;
        if (e.key === 'ArrowDown') {
            setActiveIndex((i) => Math.min(i + 1, matches.length - 1));
            e.preventDefault();
        } else if (e.key === 'ArrowUp') {
            setActiveIndex((i) => Math.max(i - 1, 0));
            e.preventDefault();
        } else if (e.key === 'Enter') {
            if (activeIndex >= 0 && matches[activeIndex]) {
                pick(matches[activeIndex]);
                e.preventDefault();
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
            e.preventDefault();
        }
    };

    function pick(account) {
        onChange(account.accountNumber);
        setOpen(false);
        setActiveIndex(-1);
        setQuery(`${account.accountNumber} — ${account.accountName}`);
    }

    const listboxId = `${id || 'bas-account-picker'}-listbox`;

    return (
        <div className="bk-account-picker" ref={wrapperRef}>
            <input
                id={id}
                type="text"
                className="bk-account-picker__input"
                placeholder="Sök konto efter nummer eller namn…"
                value={open ? query : (selected ? `${selected.accountNumber} — ${selected.accountName}` : query)}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(0); }}
                onFocus={() => { setOpen(true); setActiveIndex(matches.indexOf(selected) >= 0 ? matches.indexOf(selected) : 0); }}
                onKeyDown={handleKeyDown}
                role="combobox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
                required={!!required}
                aria-required={!!required}
                disabled={disabled}
                autoComplete="off"
            />
            {open && matches.length > 0 ? (
                <ul
                    id={listboxId}
                    role="listbox"
                    className="bk-account-picker__listbox"
                >
                    {matches.slice(0, 50).map((a, i) => (
                        <li
                            id={`${listboxId}-${i}`}
                            key={a.accountNumber}
                            role="option"
                            aria-selected={i === activeIndex}
                            className={
                                'bk-account-picker__option'
                                + (i === activeIndex ? ' is-active' : '')
                                + (a.accountNumber === value ? ' is-selected' : '')
                            }
                            onMouseDown={(e) => { e.preventDefault(); pick(a); }}
                        >
                            <span className="bk-account-picker__option-number">{a.accountNumber}</span>
                            <span className="bk-account-picker__option-name">{a.accountName}</span>
                        </li>
                    ))}
                    {filtered.length > 50 ? (
                        <li className="bk-account-picker__option bk-account-picker__option--more">
                            … {filtered.length - 50} fler konton. Skriv mer för att begränsa.
                        </li>
                    ) : null}
                </ul>
            ) : null}
            {open && matches.length === 0 ? (
                <ul id={listboxId} role="listbox" className="bk-account-picker__listbox">
                    <li className="bk-account-picker__option bk-account-picker__option--empty">
                        Inga konton matchar.
                    </li>
                </ul>
            ) : null}
        </div>
    );
}

/**
 * Parse a `filter` expression and return the matching subset of
 * accounts. Unsupported filters fall back to "no filter".
 */
function applyFilter(accounts, filter) {
    if (!Array.isArray(accounts) || accounts.length === 0) return [];
    if (!filter || typeof filter !== 'string') return accounts;

    const match = /^class:(.+)$/.exec(filter.trim());
    if (!match) return accounts;

    const spec = match[1].trim();
    // Comma-separated: "1,2,3"
    if (/^\d(\s*,\s*\d)+$/.test(spec)) {
        const set = new Set(spec.split(',').map((s) => s.trim()));
        return accounts.filter((a) => set.has(firstDigit(a.accountNumber)));
    }
    // Range: "4-6"
    const rangeMatch = /^(\d)\s*-\s*(\d)$/.exec(spec);
    if (rangeMatch) {
        const lo = parseInt(rangeMatch[1], 10);
        const hi = parseInt(rangeMatch[2], 10);
        return accounts.filter((a) => {
            const d = parseInt(firstDigit(a.accountNumber), 10);
            return Number.isFinite(d) && d >= lo && d <= hi;
        });
    }
    // Single digit: "4"
    if (/^\d$/.test(spec)) {
        return accounts.filter((a) => firstDigit(a.accountNumber) === spec);
    }
    return accounts;
}

function firstDigit(accountNumber) {
    return typeof accountNumber === 'string' && accountNumber.length > 0 ? accountNumber[0] : '';
}

export default BasAccountPicker;
export { applyFilter };
